import { Calendar, PlusCircle, Edit, Trash2 } from "feather-icons-react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import * as Yup from 'yup';
import { useFormik } from "formik";
import { 
  useCreateSalesOrderMutation,
  useGetTodaySalesOrderQuery, 
  useDeleteSalesOrderMutation, 
  useUpdateSalesOrderMutation, 
  useGetSalesOrderByIdQuery,
  
} from "../../redux/api/OrderApi";
import Table from "../../pagination/datatable";

import toast from "react-hot-toast";
import { useGetAvailableInventoryQuery, useGetProductListQuery } from "../../redux/api/productApi";
import { useGetAllCustomerQuery } from "../../redux/api/userApi";


const SaleOrderSchema = Yup.object().shape({
  saleDate: Yup.date().required('Date is required'),
});

const productSchema = Yup.object().shape({
  product: Yup.object().required('Product is required'),
  unit: Yup.string(),
  quantity: Yup.number()
    .typeError('Quantity must be a number')
    .required('Quantity is required')
    .positive('Quantity must be greater than 0')
    .test(
      'quantity-check',
      'Quantity cannot exceed available stock',
      function(value) {
        const { product } = this.parent;
        if (!product) return true;
        return value <= (this.parent.quantityLeft || 0);
      }
    ),
  saleRate: Yup.number()
    .typeError('Rate must be a number')
    .required('Rate is required')
    .positive('Rate must be greater than 0'),
  note: Yup.string(),
  customer: Yup.object().required('Customer is required'),
});

const AddSales = () => {  // Removed the customerResponse prop
  const {data: productResponse, refetch: producRefetch} = useGetProductListQuery();
  // Add customer query hook
  const {data: customerResponse, isLoading: isCustomerLoading, refetch: refetchCustomers} = useGetAllCustomerQuery();
  
  // State declarations
  const [productlist, setProductlist] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [quantityLeft, setQuantityLeft] = useState(0);
  const [SaleOrderId, setSaleOrderId] = useState(null);
  const [editingSalesOrder, setEditingSalesOrder] = useState(null);

  // API hooks
  const { data: todaySaleOrder, isLoading: isTodaySaleOrderLoading, refetch: refetchTodaySaleOrders } = useGetTodaySalesOrderQuery();
  const { data: inventoryRemaining, refetch: refetchInventoryRenaining } = useGetAvailableInventoryQuery();
  const { data: singleSalesOrder } = useGetSalesOrderByIdQuery(SaleOrderId, { skip: !SaleOrderId });
  const [createSalesOrder] = useCreateSalesOrderMutation();
  const [updateSalesOrder] = useUpdateSalesOrderMutation();
  const [deleteSaleOrderOrder] = useDeleteSalesOrderMutation();
const navigate = useNavigate(); 
const [showSupplierRedirect, setShowSupplierRedirect] = useState(false);
const [formData, setFormData] = useState(null);
const handleRouteClick = () => {
  setFormData(formik.values);
  setShowSupplierRedirect(true);
  document.querySelector('#add-units [data-bs-dismiss="modal"]')?.click();
};
useEffect(() => {
  if (showSupplierRedirect && formData) {
    navigate('/customer-list', {
      state: { 
        returnTo: '/sale-list',
        purchaseFormData: formData 
      }
    });
  }
}, [showSupplierRedirect, formData, navigate]);
  // Prepare product and customer lists
  useEffect(() => {
    const list = productResponse?.data?.map(item => ({
      value: item?._id,
      label: item?.productName,
      unit: item?.unit?.unitName || '',
      saleRate: item?.saleRate,
      quantityLeft: item?.quantity
    })) || [];
    setProductlist(list);
  }, [productResponse]);
// This effect runs after the purchases modal closes

  const customersList = customerResponse?.map(item => ({
    value: item?._id,
    label: item?.customerName
  })) || [];

  // Track quantity when product is selected
  useEffect(() => {
    if (selectedProductId && productlist.length > 0) {
      const product = productlist.find(p => p.value === selectedProductId);
      if (product) {
        setQuantityLeft(product.quantityLeft);
        formik.setFieldValue('currentProduct.quantityLeft', product.quantityLeft);
        producRefetch();
      }
    }
  }, [selectedProductId, productlist]);

  // Initialize formik
  const formik = useFormik({
    initialValues: {
      saleDate: new Date(),
      currentProduct: {
        customer: null,
        product: null,
        quantity: 0,
        quantityLeft: 0,
        unit: '',
        saleRate: '',
        note: ''
      },
      products: [],
      editIndex: null
    },
    validationSchema: SaleOrderSchema,
    onSubmit: async (values) => {
      try {
        if (values.products.length === 0) {
          toast.error("Please add at least one product");
          return;
        }
        const response = await createSalesOrder(values.products).unwrap();
        setSaleOrderId(response?._id);
        toast.success("Sale order saved successfully!");
        setQuantityLeft(0);
      } catch (error) {
        toast.error(`Failed to create sale order: ${error?.data?.message || error.message}`);
      }
    },
  });

  // Handle product selection
  const handleProductSelect = (option) => {
    setSelectedProductId(option?.value || null);
    handleCurrentProductChange('product', option);
    
    if (option) {
      handleCurrentProductChange('unit', option.unit || '');
      handleCurrentProductChange('saleRate', option.saleRate || '');
      handleCurrentProductChange('quantityLeft', option.quantityLeft || 0);
    }
  };

  // Add product to order
  const handleAddProduct = async () => {
    try {
      await productSchema.validate(formik.values.currentProduct, { abortEarly: false });
      
      const productData = {
        saleDate: formik.values.saleDate,
        productId: formik.values.currentProduct.product.value,
        customerId: formik.values.currentProduct.customer.value,
        quantity: formik.values.currentProduct.quantity,
        unit: formik.values.currentProduct.unit,
        saleRate: formik.values.currentProduct.saleRate,
        note: formik.values.currentProduct.note || ''
      };

      const response = await createSalesOrder(productData).unwrap();
      toast.success(`Sale order for ${formik.values.currentProduct.product.label} created successfully!`);
      
      refetchInventoryRenaining();
      formik.setValues({
        ...formik.values,
        products: [...formik.values.products, formik.values.currentProduct],
        currentProduct: {
          customer: null,
          product: null,
          quantity: 0,
          unit: '',
          saleRate: '',
          note: ''
        },
        editIndex: null
      });
    } catch (errors) {
      if (errors.inner) {
        const validationErrors = {};
        errors.inner.forEach(error => {
          validationErrors[error.path] = error.message;
        });
        formik.setFieldError('currentProduct', validationErrors);
      } else {
        toast.error(`Failed to add product: ${errors?.data?.message || errors.message || errors}`);
      }
    }
  };

  // Update existing product
  const handleUpdateProduct = async () => {
    if (!editingSalesOrder) return;
  
    try {
      await productSchema.validate(formik.values.currentProduct);
      const updateData = {
        saleDate: formik.values.saleDate,
        productId: formik.values.currentProduct.product.value,
        customerId: formik.values.currentProduct.customer.value,
        quantity: formik.values.currentProduct.quantity,
        unit: formik.values.currentProduct.unit,
        saleRate: formik.values.currentProduct.saleRate,
        note: formik.values.currentProduct.note || ''
      };

      await updateSalesOrder({ _id: editingSalesOrder, ...updateData }).unwrap();
      refetchTodaySaleOrders();
      refetchInventoryRenaining();
      setEditingSalesOrder(null);
      formik.resetForm();
      toast.success("Sale order updated successfully!");
    } catch (error) {
      toast.error(`Failed to update: ${error?.data?.message || error.message}`);
    }
  };

  // Remove product from order
  const handleRemoveProduct = async (id) => {
    if (id) {
      try {
        await deleteSaleOrderOrder(id).unwrap();
        toast.success('Product deleted successfully');
        refetchTodaySaleOrders();
      } catch (error) {
        toast.error('Error deleting product');
      }
    } 
  };

  // Edit existing product
  const handleEditProduct = (product) => {
    setEditingSalesOrder(product._id);
    
    const productObj = productlist?.find(p => p.value === product.product?._id) || {
      value: product.product?._id,
      label: product.product?.productName
    };
    
    const customerObj = customersList?.find(s => s.value === product.customer?._id) || {
      value: product.customer?._id,
      label: product.customer?.customerName
    };

    formik.setValues({
      ...formik.values,
      saleDate: new Date(product.saleDate),
      currentProduct: {
        customer: customerObj,
        product: productObj,
        quantity: product.quantity,
        unit: product.unit,
        saleRate: product.rate || product.saleRate,
        note: product.note || '',
        quantityLeft: productObj.quantityLeft || 0
      }
    });
    setQuantityLeft(productObj.quantityLeft || 0);
  };

  // Handle form field changes
  const handleCurrentProductChange = (field, value) => {
    const processedValue = (field === 'quantity' || field === 'saleRate') 
      ? (value === '' ? '' : Number(value))
      : value;
    
    formik.setFieldValue(`currentProduct.${field}`, processedValue);
    
    if (formik.errors.currentProduct?.[field]) {
      const newErrors = { ...formik.errors.currentProduct };
      delete newErrors[field];
      formik.setErrors({ 
        ...formik.errors,
        currentProduct: Object.keys(newErrors).length ? newErrors : undefined
      });
    }
  };

  // Helper to get field errors
  const getCurrentProductError = (field) => {
    return formik.touched.currentProduct?.[field] && formik.errors.currentProduct?.[field];
  };

  // Table columns
  const saleOrderColumns = [
    {
      title: "S No.",
      dataIndex: "serial",
      key: "serial",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Customer Name",
      key: "customerName",
      render: (record) => record?.customer?.customerName || record?.customer?.label || "-",
    },
    {
      title: "Product Name",
      key: "productName",
      render: (record) => record.product?.productName || record.product?.label || "-",
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
    },
    {
      title: "Sale Rate",
      key: "saleRate",
      render: (record) => record.saleRate || record.rate || "-",
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      render: (text) => text || "-",
    },
    {
      title: "Amount",
      key: "amount",
      render: (record) => (record.quantity * (record.saleRate || record.rate || 0)).toFixed(2),
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <>
          <button
            type="button"
            className="btn btn-sm btn-info me-2"
            onClick={() => handleEditProduct(record)}
          >
            <Edit size={14} />
          </button>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={() => handleRemoveProduct(record._id)}
          >
            <Trash2 size={14} />
          </button>
        </>
      ),
    },
  ];

  const inventoryAvailable = [
    {
      title: "S No.",
      key: "serial",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Product Id",
      dataIndex: "productId", 
      key: "productId"
    },
    {
      title: "Product Name",
      dataIndex: "productName",
      key: "productName"
    },
    {
      title: "Available Quantity",
      dataIndex: "quantity",
      key: "quantity"
    }
  ];

  return (
    <div>
      <div className="modal fade" id="add-units" >
        <div className="modal-dialog purchase modal-dialog-centered stock-adjust-modal">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header border-0 custom-modal-header">
                  <div className="page-title">
                    <h4>Add Sales</h4>
                  </div>
                  <button
                    type="button"
                    className="close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true" onClick={()=> formik.resetForm()}>×</span>
                  </button>
                </div>
                <div className="table-responsive">
                  <Table
                    columns={inventoryAvailable}
                    dataSource={Array.isArray(inventoryRemaining) ? inventoryRemaining : []}
                    rowKey={(record, index) => record._id || index}
                    default_page_size={3}
                  />
                </div>
                <div className="modal-body custom-modal-body">
                  <form onSubmit={formik.handleSubmit}>
                    <div className="row">
                      <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="input-blocks">
                          <label>Sale Date</label>
                          <div className="input-groupicon">
                            <Calendar className="info-img" />
                            <span style={{ marginLeft: '10px' }}>
                              {new Date().toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Single Product Form */}
                    <div className="row">
                      <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="input-blocks add-product">
                          <label>Customer Name</label>
                          <div className="row">
                            <div className="col-lg-10 col-sm-10 col-10">
                              <Select
                                name="customer"
                                options={customersList}
                                classNamePrefix="react-select"
                                placeholder="Choose"
                                value={formik.values.currentProduct.customer}
                                onChange={(option) => handleCurrentProductChange('customer', option)}
                                onBlur={() => formik.setFieldTouched('currentProduct.customer', true)}
                              />
                              {getCurrentProductError('customer') && (
                                <div className="text-danger">{getCurrentProductError('customer')}</div>
                              )}
                            </div>
                            <div className="col-lg-2 col-sm-2 col-2 ps-0">
                              <div className="add-icon tab" onClick={handleRouteClick}>
                                <Link to="#">
                                  <PlusCircle className="feather-plus-circles" />
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="input-blocks">
                          <label>Product Name</label>
                          <Select
                            options={productlist}
                            classNamePrefix="react-select"
                            placeholder="Choose"
                            value={formik.values.currentProduct.product}
                            onChange={handleProductSelect}
                            onBlur={() => formik.setFieldTouched('currentProduct.product', true)}
                          />
                          {getCurrentProductError('product') && (
                            <div className="text-danger">{getCurrentProductError('product')}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-2 col-md-6 col-sm-12">
                        <div className="input-blocks">
                          <label>Quantity</label>
                          <input
                            type="number"
                            className="form-control"
                            name="quantity"
                            value={formik.values.currentProduct.quantity}
                            onChange={(e) => handleCurrentProductChange('quantity', e.target.value)}
                            onBlur={() => formik.setFieldTouched('currentProduct.quantity', true)}
                          />
                          {getCurrentProductError('quantity') && (
                            <div className="text-danger">{getCurrentProductError('quantity')}</div>
                          )}
                        </div>
                      </div>
                      {/* <div className="col-lg-2 col-md-6 col-sm-12">
                        <div className="input-blocks">
                          <label>Quantity left</label>
                          <input
                            type="number"
                            className="form-control"
                            name="quantityLeft"
                            value={formik.values.currentProduct.quantityLeft || ''}
                            readOnly
                          />
                        </div>
                      </div> */}
                      <div className="col-lg-2 col-md-6 col-sm-12">
                        <div className="input-blocks">
                          <label>Unit</label>
                          <input
                            type="text"
                            className="form-control"
                            name="unit"
                            value={formik.values.currentProduct.unit || ''}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row mt-3">
                      <div className="col-lg-2 col-md-6 col-sm-12">
                        <div className="input-blocks">
                          <label>Sale Rate</label>
                          <input
                            type="number"
                            className="form-control"
                            name="saleRate"
                            value={formik.values.currentProduct.saleRate}
                            onChange={(e) => handleCurrentProductChange('saleRate', e.target.value)}
                            onBlur={() => formik.setFieldTouched('currentProduct.saleRate', true)}
                          />
                          {getCurrentProductError('saleRate') && (
                            <div className="text-danger">{getCurrentProductError('saleRate')}</div>
                          )}
                        </div>
                      </div>
                      
                      <div className="col-lg-2 col-md-6 col-sm-12">
                        <div className="input-blocks">
                          <label>Note</label>
                          <input
                            type="text"
                            className="form-control"
                            name="note"
                            value={formik.values.currentProduct.note}
                            onChange={(e) => handleCurrentProductChange('note', e.target.value)}
                            onBlur={() => formik.setFieldTouched('currentProduct.note', true)}
                          />
                        </div>
                      </div>
                      
                      <div className="col-lg-2 col-md-6 col-sm-12 d-flex align-items-end">
                        <div className="input-blocks w-100 d-flex justify-content-end">
                          {editingSalesOrder ? (
                            <>
                              <button 
                                type="button"
                                className="btn btn-success me-2"
                                onClick={handleUpdateProduct}
                              >
                                Update
                              </button>
                              <button 
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => {
                                  setEditingSalesOrder(null);
                                  formik.resetForm();
                                }}
                              >
                                Clear
                              </button>
                            </>
                          ) : (
                            <button 
                              type="button"
                              className="btn btn-primary"
                              onClick={handleAddProduct}
                            >
                              Add Product
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Products Table */}
                    <div className="row mt-4">
                      <div className="col-lg-12">
                        <div className="modal-body-table">
                          <div className="table-responsive">
                            <Table
                              columns={saleOrderColumns}
                              dataSource={Array.isArray(todaySaleOrder) ? todaySaleOrder : []}
                              rowKey={(record, index) => record._id || index}
                              default_page_size={3}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row mt-3">
                      <div className="col-lg-12">
                        <div className="modal-footer-btn">
                          <button
                            type="button"
                            className="btn btn-cancel me-2"
                            data-bs-dismiss="modal"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSales;